import { useState } from 'react';
import { Keyboard, Loader2 } from 'lucide-react';

interface ShortcutsToggleProps {
  enabled: boolean;
  onToggle: (enabled: boolean) => Promise<void>;
  disabled?: boolean;
}

/**
 * Toggle para habilitar/desabilitar atalhos de teclado
 * Exibido no footer da página de ferramentas
 */
export function ShortcutsToggle({ enabled, onToggle, disabled }: ShortcutsToggleProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleToggle = async () => {
    if (disabled || isLoading) return;

    setIsLoading(true);
    try {
      await onToggle(!enabled);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleToggle}
      disabled={disabled || isLoading}
      title={enabled ? 'Atalhos habilitados (F1 para desligar)' : 'Atalhos desabilitados (F1 para ativar)'}
      className={`
        flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium
        transition-all duration-200
        ${disabled || isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${enabled
          ? 'bg-blue-600/30 text-blue-300 border border-blue-500/50 hover:bg-blue-600/40'
          : 'bg-navy-800/50 text-gray-400 border border-navy-600/50 hover:bg-navy-700/50'
        }
      `}
    >
      {isLoading ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : (
        <Keyboard className={`w-3.5 h-3.5 ${enabled ? 'text-blue-400' : 'text-gray-500'}`} />
      )}
      <span className="hidden sm:inline">
        {isLoading ? 'Aguarde...' : enabled ? 'Atalhos ON' : 'Atalhos OFF'}
      </span>
      <span className="sm:hidden">
        {isLoading ? '...' : enabled ? 'ON' : 'OFF'}
      </span>
    </button>
  );
}
